import axios from "axios";
import { EnvironmentRegistry } from "./registry/environmentRegistry";
import { getAccessToken, getLob } from "./authenticationService";
import { ChannelkartNetworkGet } from "./networkService";

// Types
export interface ColumnMetadata {
  type: string;
  label: string;
  default?: boolean;
}

export interface FilterConfig {
  id: string;
  label: string;
  filter: 'daterange' | 'periodfilter' | 'monthyear' | 'daterangeAndPeriod' | null;
}

export interface GlobalFilterConfig {
  id: string;
  label: string;
  api: string;
  parent?: string;
  multiSelect: boolean;
  apiType?: string;
}

// Updated interfaces to match new API structure
export interface TemplateOption {
  id: string;
  name: string;
  type: string;
  role: string;
  filter: string | null;
  globalFilters: GlobalFilterConfig[] | null;
  // Additional fields that might be present in the response
  query?: string;
  default_columns?: string;
  all_columns?: Record<string, ColumnMetadata>;
  entities?: string;
  reportTemplateConfig?: Record<string, any>;
}

export interface SelectedColumn {
  id: string;
  originalColumn: string;
  displayName: string;
  customHeader: string;
  columnType: string;
  dateFormat?: string;
  numberFormat?: string;
  textFormat?: string;
  hasError?: boolean;
}

// New interfaces for the updated API structure
export interface ReportHeader {
  key: string;
  value: string;
  position: string;
}

export interface ReportColumn {
  id: string;
  format: string;
}

export interface ReportExtendedAttributes {
  headers: ReportHeader[];
  templateName: string;
  columns: ReportColumn[];
}

export interface ReportInfo {
  type: string;
  format: string;
  baseQuery: string;
  fetchLimit: string;
  designations: string;
  implementation: string;
  reportDelimiter: string;
  id: string;
  name: string;
  extendedAttributes: ReportExtendedAttributes;
}

export interface ReportUI {
  id: string;
  name: string;
  type: string;
  role: string;
  filter: string | null;
  globalFilters: GlobalFilterConfig[] | null;
  emailReportEnabled?: boolean;
}

export interface ReportConfig {
  reportInfo: ReportInfo;
  reportUI: ReportUI;
}

// Legacy interface for backward compatibility
export interface LegacyReportConfig {
  name: string;
  template: string;
  columns: Omit<SelectedColumn, 'id'>[];
  query: string;
  isEmail?: boolean;
}

// Add ReportService network helpers
async function ReportServiceGet(url: string, headers: any = {}) {
  const baseUrl = EnvironmentRegistry.getReportServiceUrl();
  const token = getAccessToken();
  return axios.get(baseUrl + url, {
    headers: {
      Authorization: `Bearer ${token}`,
      lob: getLob(),
      ...headers,
    },
  });
}

async function ReportServicePost(url: string, data?: any, headers: any = {}) {
  const baseUrl = EnvironmentRegistry.getReportServiceUrl();
  const token = getAccessToken();
  return axios.post(baseUrl + url, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      lob: getLob(),
      ...headers,
    },
  });
}

async function ReportServicePut(url: string, data?: any, headers: any = {}) {
  const baseUrl = EnvironmentRegistry.getReportServiceUrl();
  const token = getAccessToken();
  return axios.put(baseUrl + url, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      lob: getLob(),
      ...headers,
    },
  });
}

async function ReportServiceDelete(url: string, headers: any = {}) {
  const baseUrl = EnvironmentRegistry.getReportServiceUrl();
  const token = getAccessToken();
  return axios.delete(baseUrl + url, {
    headers: {
      Authorization: `Bearer ${token}`,
      lob: getLob(),
      ...headers,
    },
  });
}

// Service class
export class TemplateReportService {
  static async fetchTemplates(): Promise<TemplateOption[]> {
    try {
      const response = await ReportServiceGet('/v1/reportTemplate');
      // Handle the actual API response structure: response.data.features
      if (response && response.data && response.data.features && Array.isArray(response.data.features)) {
        // Transform the features array to match our TemplateOption interface
        const transformedTemplates = response.data.features.map((feature: any) => ({
          id: feature.templateName,
          name: feature.templateName,
          type: "TemplateReport",
          role: "admin", // Default role, can be made dynamic later
          filter: feature.filter,
          globalFilters: feature.globalFilters,
          query: feature.reportTemplateConfig?.baseQuery || "",
          default_columns: Object.entries(feature.allColumns || {})
            .filter(([_, col]: [string, any]) => col.default)
            .map(([key, _]: [string, any]) => key)
            .join(", "),
          all_columns: feature.allColumns || {},
          entities: "",
          reportTemplateConfig: feature.reportTemplateConfig || {}
        }));
      
        return transformedTemplates;
      }
      
      console.warn("Unexpected response structure, returning empty array:", response);
      return [];
    } catch (error) {
      console.error("Error fetching templates:", error);
      throw new Error("Failed to load report templates");
    }
  }

  static async checkAdminStatus(): Promise<boolean> {
    try {
      const authContext = localStorage.getItem('authContext');
      if (!authContext) return false;
  
      const parsedContext = JSON.parse(authContext);
  
      const roles = parsedContext?.user?.roles;
      if (!Array.isArray(roles)) return false;
  
      // Check if any role has id === 'role_admin'
      return roles.some((role: any) => role?.id === 'role_admin');
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  // Helper method to convert to flat payload for report creation
  static buildFlatTemplatePayload(
    legacyConfig: LegacyReportConfig,
    template: TemplateOption,
    emailReportEnabled: boolean
  ) {
    // Map columns to the required format
    const selectedColumns = legacyConfig.columns.map((col, index) => {
      const colObj: any = {
        format: col.dateFormat || col.numberFormat || 'text',
        key: col.originalColumn,
        value: col.customHeader,
        position: index + 1,
        type: col.columnType, // Add the type property
      };
      if (col.hasOwnProperty('default')) {
        colObj.default = (col as any).default;
      }
      return colObj;
    });
    return {
      reportName: legacyConfig.name,
      templateName: template.name,
      selectedColumns,
      emailReportEnabled,
    };
  }

  static async createReport(config: LegacyReportConfig, template: TemplateOption, emailReportEnabled: boolean): Promise<void> {
    try {
      const payload = this.buildFlatTemplatePayload(config, template, emailReportEnabled);
      await ReportServicePost('/v1/reportTemplate/reportInfo', payload);
    } catch (error) {
      console.error("Error creating report:", error);
      throw new Error("Failed to create report configuration");
    }
  }

  // Helper method to determine column format
  private static getColumnFormat(column: Omit<SelectedColumn, 'id'>): string {
    if (column.dateFormat && column.dateFormat !== ' ') {
      return column.dateFormat;
    }
    if (column.numberFormat && column.numberFormat !== ' ') {
      return column.numberFormat;
    }
    if (column.columnType === 'date' || column.columnType === 'datetime') {
      return 'yyyy-mm-dd HH:mm:ss';
    }
    if (column.columnType === 'number') {
      return 'none';
    }
    return 'string';
  }

  static async sendReportByEmail(reportConfig: ReportConfig & { email: string }): Promise<void> {
    try {
      const response = await fetch('/api/reports/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportConfig),
      });

      if (!response.ok) {
        throw new Error('Failed to send report via email');
      }
    } catch (error) {
      console.error('Error sending report via email:', error);
      throw error;
    }
  }

  // Fetch all templates
  static async getAllTemplates(): Promise<any[]> {
    const response = await ReportServiceGet('/v1/reportTemplate');
    return response?.data?.features || [];
  }

  // Fetch a single template by name
  static async getTemplate(templateName: string): Promise<any> {
    const response = await ReportServiceGet(`/v1/reportTemplate/${encodeURIComponent(templateName)}`);
    return response?.data || null;
  }

  // Create a new template
  static async createTemplate(templateData: any): Promise<any> {
    const internalToken = localStorage.getItem('token') || '';
    const response = await ReportServicePost('/v1/reportTemplate/', templateData, { internal_token: internalToken });
    return response?.data || null;
  }

  // Update an existing template
  static async updateTemplate(templateName: string, templateData: any): Promise<any> {
    const internalToken = localStorage.getItem('token') || '';
    const response = await ReportServicePut(`/v1/reportTemplate/${encodeURIComponent(templateName)}`, templateData, { internal_token: internalToken });
    return response?.data || null;
  }

  // Delete a template
  static async deleteTemplate(templateName: string): Promise<any> {
    const response = await ReportServiceDelete(`/v1/reportTemplate/${encodeURIComponent(templateName)}`);
    return response?.data || null;
  }

  // Fetch all editable reports (those with extendedAttributes.templateName)
  static async getEditableReports(): Promise<any[]> {
    const response = await ChannelkartNetworkGet('/v1/registry/data/ReportInfo');
    const reports = response?.data?.features || [];
    // Filter reports with extendedAttributes.templateName
    return reports.filter((r: any) => r?.extendedAttributes && r.extendedAttributes.templateName);
  }

  // Update an existing report
  static async updateReport(reportData: any): Promise<any> {
    try {
      // Structure the payload to match the create report format
      const updatePayload = {
        reportInfo: {
          type: reportData.type || "CUSTOM",
          format: reportData.format || "csv",
          baseQuery: reportData.baseQuery || "",
          fetchLimit: reportData.fetchLimit || "10000",
          designations: reportData.designations || "admin",
          implementation: reportData.implementation || "com.applicate.services.channelkart.reports.CustomTemplateReport",
          reportDelimiter: reportData.reportDelimiter || ",",
          id: reportData.id,
          name: reportData.name,
          extendedAttributes: reportData.extendedAttributes
        },
        reportUI: {
          id: reportData.id,
          name: reportData.name,
          type: "TemplateReport",
          role: "admin",
          filter: reportData.filter || null,
          globalFilters: reportData.globalFilters || null
        }
      };  
      // Use the same endpoint as createReport but with PUT method
      const response = await ReportServicePost('/v1/reportTemplate/reportInfo?source=portal', updatePayload);
      return response?.data || null;
    } catch (error) {
      console.error("Error updating report:", error);
      throw new Error("Failed to update report");
    }
  }
}
  
  // Utility functions
  export const formatDisplayName = (displayName: string): string => {
    return displayName
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };
  
  export const isDateColumn = (columnMeta?: ColumnMetadata): boolean => {
    return columnMeta?.type === "date" || columnMeta?.type === "datetime";
  };
  
  export const isNumberColumn = (columnMeta?: ColumnMetadata): boolean => {
    return columnMeta?.type === "number" 
  };
  
  export const getDefaultNumberFormat = (columnMeta?: ColumnMetadata): string => {
    if (columnMeta?.type === "number") {
      return "number";
    }
    return "number";
  };
  
  export const generateCustomQuery = (template: TemplateOption, selectedColumns: SelectedColumn[]): string => {
    if (!template || selectedColumns.length === 0) return "";
  
    const baseQuery = template.query || "";
    const selectedColumnsStr = selectedColumns
      .map((col) => col.originalColumn)
      .join(", ");
  
    return baseQuery.replace(
      /select\s+.*?\s+from/i,
      `SELECT ${selectedColumnsStr} FROM`
    );
  }; 